import { useAppSelector } from "@/redux/hooks";
import BackControls from "../components/BackControls";
import { ArrowLeft, ArrowRight, GraduationCap, Info, Search } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Department, Track } from "../data/sharedTypes";
import {
    type FmCountry,
    type FmDiscipline,
    getFmDisciplineNameKey,
    getFmSpecialtyNameKey,
    getFmSpecialtiesForCountryAndCollege,
    isFmHsOnly,
} from "../data/foreignMissionsData";
import StepHeader from "../StepHeader";
import FlagIcon from "../components/FlagIcon";

/** FM specialties that should not display the "rate not announced / not offered
 *  last year" note even when they have no `minAcceptedLastYear` value —
 *  legacy 2025/2026-plan rows, where "not offered last year" would be wrong,
 *  so their card shows name + apply-floor only. */
const FM_SPECIALTIES_WITHOUT_RATE_NOTE = new Set([
    "FM-STA-AU", "FM-MAT-AU", "FM-THM-NZ", "FM-AGR-NZ", "FM-FPE-NZ",
    "FM-MPN-IE", "FM-IDN-IE", "FM-HHD-IE", "FM-HPS-IE", "FM-CGD-IE", "FM-MUL-IE", "FM-AGR-IE",
]);

interface Props {
    universityNameKey: string;
    country: FmCountry;
    discipline: FmDiscipline;
    track: Track;
    onBack: () => void;
    onSelectSpecialty: (department: Department) => void;
}

/**
 * Foreign Missions — leaf specialty picker (FM-only step).
 *
 * After Track → Country → Discipline, this lists the actual specialties
 * (medicine, dentistry, pharmacy, biomedical_engineering, …) the student
 * can apply to. Each tile shows:
 *   - HS minimum to apply (المعدل column in the PDF)
 *   - Last-year accepted equivalent rate (when published in the supplement)
 */
export default function ChooseFmSpecialty({
    universityNameKey,
    country,
    discipline,
    track,
    onBack,
    onSelectSpecialty,
}: Props) {
    const { t } = useTranslation();
    const { locale } = useAppSelector((state) => state);
    const [searchQuery, setSearchQuery] = useState("");

    const specialties = getFmSpecialtiesForCountryAndCollege(country.key, discipline.key, track);

    const filtered = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        if (!q) return specialties;
        return specialties.filter((dept) =>
            t(getFmSpecialtyNameKey(dept)).toLowerCase().includes(q),
        );
    }, [specialties, searchQuery, t]);

    return (
        <div className="flex flex-col gap-y-6">
            <BackControls onBack={onBack} />

            <StepHeader
                title={t("dream_university_choose_specialty")}
                subtitle={`${discipline.emoji} ${t(getFmDisciplineNameKey(discipline.key, country.key))}`}
            />

            {/* Selection trail */}
            <div className="flex flex-wrap items-center justify-center gap-2">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
                    🌍
                    {t(universityNameKey)}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-gray-100 text-gray-700">
                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-white ring-1 ring-gray-200 overflow-hidden">
                        <FlagIcon iso={country.iso} className="w-full h-full" />
                    </span>
                    {t(`dream_university_country_${country.key}`)}
                </span>
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full ring-1 ${
                    track === "scientific"
                        ? "bg-blue-50 text-blue-700 ring-blue-200"
                        : "bg-purple-50 text-purple-700 ring-purple-200"
                }`}>
                    {t(`dream_university_track_${track}`)}
                </span>
                <span className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full bg-theme-50 text-theme-700 ring-1 ring-theme-200">
                    <span>{discipline.emoji}</span>
                    {t(getFmDisciplineNameKey(discipline.key, country.key))}
                </span>
            </div>

            {specialties.length > 4 && (
                <div className="relative">
                    <Search className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder={t("dream_university_search_specialty")}
                        className="w-full ps-9 pe-3 py-2.5 text-sm bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-theme-500 focus:border-transparent placeholder:text-gray-400"
                    />
                </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {filtered.map((dept) => (
                    <button
                        key={dept.code}
                        onClick={() => onSelectSpecialty(dept)}
                        className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-theme-500 hover:shadow-md transition-all duration-200 text-start"
                    >
                        <div className="w-9 h-9 shrink-0 bg-theme-100 rounded-lg flex items-center justify-center">
                            <GraduationCap className="w-4.5 h-4.5 text-theme-700" />
                        </div>
                        <div className="flex flex-col gap-0.5 flex-1">
                            <span className="text-sm font-medium text-gray-800">
                                {t(getFmSpecialtyNameKey(dept))}
                            </span>
                            {dept.equivalentRate > 0 && (
                                <span className="text-xs text-amber-600">
                                    {t("dream_university_fm_specialty_min_hs_to_apply", { rate: dept.equivalentRate })}
                                </span>
                            )}
                            {dept.minAcceptedLastYear !== undefined ? (
                                <span className="text-xs text-emerald-700">
                                    {isFmHsOnly(dept.collegeKey)
                                        ? t("dream_university_fm_specialty_last_year_hs_rate", { rate: dept.minAcceptedLastYear })
                                        : t("dream_university_fm_specialty_last_year_equivalent_rate", { rate: dept.minAcceptedLastYear })}
                                </span>
                            ) : FM_SPECIALTIES_WITHOUT_RATE_NOTE.has(dept.code) ? null : (
                                <span className="inline-flex items-start gap-1 text-xs text-blue-600">
                                    <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    <span>{t("dream_university_specialty_rate_not_announced")}</span>
                                </span>
                            )}
                        </div>
                    </button>
                ))}
            </div>

            {specialties.length === 0 && (
                <p className="text-center text-sm text-gray-500 py-6">
                    {t("dream_university_fm_no_specialties_in_discipline")}
                </p>
            )}
        </div>
    );
}
